/*
 * Constants.
 */

export const globalNamespace = 'global';

/**
 * There is a perennial request from Product/Marketing teams to link to specific namespaced settings pages.
 * The problem being that the first team namespace is a dynamic value, complicating the URL in the marketing
 * email copy, because each company needs a unique email copy/URL. firstTeamUserIsAdminOfNamespaceAlias allows
 * engineers to craft a universal URL linking to a specific namespaced settings page. So the marketing email
 * only needs one copy/URL for everyone.
 *
 * EXAMPLE: https://front.frontapp.com/settings/tim:first-admin-of/ai
 */
const firstTeamUserIsAdminOfNamespaceAlias = 'tim:first-admin-of';

/*
 * Helpers.
 */

export function isTeammateNamespace(namespace: string) {
  return namespace.startsWith('tea:');
}

export function isTeamNamespace(namespace: string) {
  return namespace.startsWith('tim:');
}

export function computeIsGlobalNamespace(namespace: string) {
  return namespace === globalNamespace;
}

export function isTeamOrGlobalNamespace(namespace: string) {
  return isTeamNamespace(namespace) || computeIsGlobalNamespace(namespace);
}

export function computeIsFirstTeamUserIsAdminOfNamespaceAlias(namespace: string) {
  return namespace === firstTeamUserIsAdminOfNamespaceAlias;
}

/**
 * A discussion or task are private if they are in a private inbox or no inbox (the global namespace).
 */
export function isTeammateOrGlobalNamespace(namespace: string) {
  return isTeammateNamespace(namespace) || computeIsGlobalNamespace(namespace);
}

export function parseTeammateNamespace(namespace: string) {
  return isTeammateNamespace(namespace) ? Number(namespace.slice(4)) : undefined;
}

export function parseTeamNamespace(namespace: string) {
  return isTeamNamespace(namespace) ? Number(namespace.slice(4)) : undefined;
}

/*
 * Application namespace helpers.
 */

interface ApplicationParsedTeamNamespace {
  type: 'team';
  id: number;
}

interface ApplicationParsedTeammateNamespace {
  type: 'teammate';
  id: number;
}

interface ApplicationParsedGlobalNamespace {
  type: 'global';
}

export type ApplicationParsedNamespace =
  | ApplicationParsedTeamNamespace
  | ApplicationParsedTeammateNamespace
  | ApplicationParsedGlobalNamespace;

export function parseNamespaceForApplication(namespace: string): ApplicationParsedNamespace {
  const teammateId = parseTeammateNamespace(namespace);
  if (teammateId) {
    return {type: 'teammate', id: teammateId};
  }

  const teamId = parseTeamNamespace(namespace);
  if (teamId) {
    return {type: 'team', id: teamId};
  }

  if (computeIsGlobalNamespace(namespace)) {
    return {type: globalNamespace};
  }

  throw new Error(`Unable to parse namespace ${namespace}`);
}
